// Experience level auto-fill
//
// When the user picks a "Level of experience" radio on the
// "Profile and conditions" step, prefill the Minimum and Maximum
// years-of-experience selects according to the chosen level.
//
// The mapping comes from data attributes (data-min-years / data-max-years)
// rendered on each radio in candidate_background.slim, so it stays driven
// by Ruby code.
//
// The auto-fill only kicks in when the user has not already set a value,
// or when the current value comes from a previous auto-fill. After the
// user manually edits Min/Max, those choices stay until the user picks
// another level (the AC requires they remain editable).

(() => {
  const initialize = () => {
    const radios = document.querySelectorAll<HTMLInputElement>(
      'input.js-experience-level-input'
    );
    if (radios.length === 0) return;

    const minSelect = document.querySelector<HTMLSelectElement>('select.js-experience-min');
    const maxSelect = document.querySelector<HTMLSelectElement>('select.js-experience-max');
    if (!minSelect || !maxSelect) return;

    // Avoid double-binding when `turbolinks:load` fires again on a cached
    // restore that reuses the same DOM nodes.
    if (minSelect.dataset.autofillBound === 'true') return;
    minSelect.dataset.autofillBound = 'true';

    let autofilledMin: string | null = null;
    let autofilledMax: string | null = null;

    const setSelectValue = (
      select: HTMLSelectElement,
      value: string,
      previousAutofill: string | null,
    ): string | null => {
      // Only overwrite when the field is empty or still holds the previous
      // auto-fill value — never stomp on a manual choice.
      if (select.value === '' || select.value === previousAutofill) {
        select.value = value;
        // Trigger change so selectize/listeners stay in sync.
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return value;
      }
      return select.value === value ? value : null;
    };

    const applyAutofill = (radio: HTMLInputElement) => {
      const min = radio.dataset.minYears;
      const max = radio.dataset.maxYears;
      if (min === undefined || max === undefined) return;

      autofilledMin = setSelectValue(minSelect, min, autofilledMin);
      autofilledMax = setSelectValue(maxSelect, max, autofilledMax);
    };

    radios.forEach(radio => {
      radio.addEventListener('change', () => {
        if (radio.checked) applyAutofill(radio);
      });
    });

    // AI-assisted import renders the matching radio as already `checked`
    // server-side without filling the range, so seed Min/Max once on load.
    // Only do this when NO range was persisted yet (both selects empty).
    //
    // ILJ-1157: "Any" is `include_blank` → an empty string, indistinguishable
    // from "unset" in the guard. Re-running the on-load autofill for a job
    // that already has a range (e.g. min=5, max=Any) stomped the deliberate
    // "Any" with the level default on every step navigation. Gating on
    // "both empty" keeps the AI-import seed while leaving edited ranges alone.
    const checked = Array.from(radios).find(radio => radio.checked);
    if (checked && minSelect.value === '' && maxSelect.value === '') {
      applyAutofill(checked);
    }
  };

  // The post wizard is Turbolinks-driven, so bind to `turbolinks:load`
  // (fires on the initial navigation and on cached/secondary visits)
  // rather than `DOMContentLoaded`, which won't fire on Turbolinks visits.
  // The guard inside `initialize` keeps listeners from binding twice.
  document.addEventListener('turbolinks:load', initialize);
})();
